import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isSameDay, isSameMonth, addWeeks, addMonths } from 'date-fns';
import { nl } from 'date-fns/locale';
import { formatNumber, formatPercentage, formatDate } from '../utils/helpers';
import { 
  Plus, Calendar as CalendarIcon, Users, XCircle, TrendingUp, ChevronLeft, ChevronRight,
  Clock, User, Mail, Phone, ExternalLink, Check, X, Eye, Trash2, Edit, Globe, AlertCircle, Search, Filter, RotateCcw
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const ReserveringenBezettingPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [reservations, setReservations] = useState([]);
  const [occupancy, setOccupancy] = useState([]);
  const [locations, setLocations] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [reservationDialog, setReservationDialog] = useState(false);
  const [occupancyDialog, setOccupancyDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [activeTab, setActiveTab] = useState('agenda');
  const [agendaView, setAgendaView] = useState('week'); // day, week, month
  const [currentDate, setCurrentDate] = useState(new Date());
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [searchGuestCount, setSearchGuestCount] = useState('');

  const [reservationForm, setReservationForm] = useState({
    date: new Date().toISOString().split('T')[0],
    time: '19:00',
    guest_name: '',
    guest_count: '2',
    phone: '',
    email: '',
    location_id: '',
    reservation_type: 'diner',
    notes: ''
  });

  const [occupancyForm, setOccupancyForm] = useState({
    date: new Date().toISOString().split('T')[0],
    actual_guests: '',
    capacity: '',
    location_id: ''
  });

  const reservationTypes = [
    { value: 'ontbijt', label: 'Ontbijt', color: 'bg-amber-100 text-amber-700' },
    { value: 'lunch', label: 'Lunch', color: 'bg-green-100 text-green-700' },
    { value: 'diner', label: 'Diner', color: 'bg-blue-100 text-blue-700' },
    { value: 'borrel', label: 'Borrel', color: 'bg-purple-100 text-purple-700' },
    { value: 'feest', label: 'Feest', color: 'bg-pink-100 text-pink-700' },
    { value: 'vergadering', label: 'Vergadering', color: 'bg-slate-100 text-slate-700' },
    { value: 'anders', label: 'Anders', color: 'bg-stone-100 text-stone-700' }
  ];

  const statusConfig = {
    in_afwachting: { label: 'In afwachting', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
    bevestigd: { label: 'Bevestigd', color: 'bg-emerald-100 text-emerald-700', icon: Check },
    geannuleerd: { label: 'Geannuleerd', color: 'bg-red-100 text-red-700', icon: X },
    'no-show': { label: 'No-show', color: 'bg-orange-100 text-orange-700', icon: XCircle },
    voltooid: { label: 'Voltooid', color: 'bg-blue-100 text-blue-700', icon: Check }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const startDate = startOfMonth(addMonths(currentDate, -1));
      const endDate = endOfMonth(addMonths(currentDate, 1));
      const params = {
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd')
      };

      const [resRes, occRes, locRes, pendingRes, emailRes] = await Promise.all([
        api.get('/reservations', { params }),
        api.get('/occupancy', { params }),
        api.get('/locations'),
        api.get('/reservations/pending-count'),
        api.get('/reservations/email-config')
      ]);
      setReservations(resRes.data);
      setOccupancy(occRes.data);
      setLocations(locRes.data);
      setPendingCount(pendingRes.data.count);
      setEmailConfigured(emailRes.data.configured);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  // Filter reservations based on search criteria
  const getFilteredReservations = () => {
    let filtered = [...reservations];
    
    // Text search (name, phone, reservation ID)
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(r => 
        r.guest_name?.toLowerCase().includes(query) ||
        r.phone?.toLowerCase().includes(query) ||
        r.id?.toLowerCase().includes(query)
      );
    }
    
    // Date filter
    if (searchDate) {
      filtered = filtered.filter(r => r.date === searchDate);
    }
    
    // Guest count filter (ignore "all" or empty)
    if (searchGuestCount && searchGuestCount !== 'all') {
      const count = parseInt(searchGuestCount);
      filtered = filtered.filter(r => r.guest_count === count);
    }
    
    // Sort: pending first, then by date/time
    return filtered.sort((a, b) => {
      if (a.status === 'in_afwachting' && b.status !== 'in_afwachting') return -1;
      if (b.status === 'in_afwachting' && a.status !== 'in_afwachting') return 1;
      return new Date(a.date + 'T' + a.time) - new Date(b.date + 'T' + b.time);
    });
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchDate('');
    setSearchGuestCount('');
  };
  
  const hasActiveFilters = searchQuery || searchDate || (searchGuestCount && searchGuestCount !== 'all');

  const handleAddReservation = async (e) => {
    e.preventDefault();
    try {
      await api.post('/reservations', {
        ...reservationForm,
        guest_count: parseInt(reservationForm.guest_count) || 2,
        location_id: reservationForm.location_id || null
      });
      toast.success('Reservering toegevoegd');
      setReservationDialog(false);
      resetReservationForm();
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddOccupancy = async (e) => {
    e.preventDefault();
    try {
      await api.post('/occupancy', null, {
        params: {
          date: occupancyForm.date,
          actual_guests: parseInt(occupancyForm.actual_guests) || 0,
          capacity: parseInt(occupancyForm.capacity) || 0,
          location_id: occupancyForm.location_id || null
        }
      });
      toast.success('Bezettingsgraad toegevoegd');
      setOccupancyDialog(false);
      setOccupancyForm({ date: new Date().toISOString().split('T')[0], actual_guests: '', capacity: '', location_id: '' });
      fetchData();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      const response = await api.put(`/reservations/${id}/status`, { status });
      const { email_sent, email_error } = response.data;
      
      if (status === 'bevestigd') {
        if (email_sent) {
          toast.success('Reservering bevestigd en bevestigingsmail verzonden!');
        } else if (email_error === 'RESEND_API_KEY niet geconfigureerd') {
          toast.success('Reservering bevestigd (e-mail nog niet geconfigureerd)');
        } else {
          toast.success('Reservering bevestigd');
          if (email_error) {
            toast.error(`E-mail kon niet verzonden worden: ${email_error}`);
          }
        }
      } else {
        toast.success('Status bijgewerkt');
      }
      
      setDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij bijwerken');
    }
  };

  const handleDeleteReservation = async (id) => {
    if (!window.confirm('Reservering verwijderen?')) return;
    try {
      await api.delete(`/reservations/${id}`);
      toast.success('Reservering verwijderd');
      setDetailDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const resetReservationForm = () => {
    setReservationForm({
      date: new Date().toISOString().split('T')[0],
      time: '19:00',
      guest_name: '',
      guest_count: '2',
      phone: '',
      email: '',
      location_id: '',
      reservation_type: 'diner',
      notes: ''
    });
  };

  const openQuickAdd = (date) => {
    setReservationForm(prev => ({
      ...prev,
      date: format(date, 'yyyy-MM-dd')
    }));
    setReservationDialog(true);
  };

  // Navigation
  const navigatePrev = () => {
    if (agendaView === 'day') setCurrentDate(addDays(currentDate, -1));
    else if (agendaView === 'week') setCurrentDate(addWeeks(currentDate, -1));
    else setCurrentDate(addMonths(currentDate, -1));
  };

  const navigateNext = () => {
    if (agendaView === 'day') setCurrentDate(addDays(currentDate, 1));
    else if (agendaView === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addMonths(currentDate, 1));
  };

  const goToToday = () => setCurrentDate(new Date());

  // Get reservations for a specific date
  const getReservationsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return reservations.filter(r => r.date === dateStr).sort((a, b) => a.time.localeCompare(b.time));
  };

  // Calculate metrics
  const totalReservations = reservations.length;
  const confirmedReservations = reservations.filter(r => r.status === 'bevestigd').length;
  const pendingReservations = reservations.filter(r => r.status === 'in_afwachting').length;
  const noShows = reservations.filter(r => r.status === 'no-show').length;
  const noShowPercentage = totalReservations > 0 ? (noShows / totalReservations) * 100 : 0;
  const totalExpectedGuests = reservations.filter(r => r.status === 'bevestigd' || r.status === 'in_afwachting').reduce((sum, r) => sum + (r.guest_count || 0), 0);

  // Generate calendar days
  const generateWeekDays = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    return Array.from({ length: 7 }, (_, i) => addDays(start, i));
  };

  const generateMonthDays = () => {
    const start = startOfMonth(currentDate);
    const end = endOfMonth(currentDate);
    const startWeek = startOfWeek(start, { weekStartsOn: 1 });
    const endWeek = endOfWeek(end, { weekStartsOn: 1 });
    const days = [];
    let day = startWeek;
    while (day <= endWeek) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  };

  const getTypeConfig = (type) => reservationTypes.find(t => t.value === type) || reservationTypes[6];

  const previewUrl = `${window.location.origin}/reserveren`;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#C2410C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="reserveringen-bezetting-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Reserveringen & Bezetting
          </h1>
          <p className="text-[#57534E] mt-1">Beheer reserveringen en volg bezettingsgraad</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/reserveren"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 transition-colors"
          >
            <Globe className="w-4 h-4" />
            Publieke pagina
            <ExternalLink className="w-3 h-3" />
          </a>
          <Dialog open={reservationDialog} onOpenChange={setReservationDialog}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-reservation-btn">
                <Plus className="w-4 h-4 mr-2" />
                Reservering
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Nieuwe reservering</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddReservation} className="space-y-4 pt-4">
                {/* Type selection */}
                <div className="space-y-2">
                  <Label>Type</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {reservationTypes.map(type => (
                      <button
                        key={type.value}
                        type="button"
                        onClick={() => setReservationForm({ ...reservationForm, reservation_type: type.value })}
                        className={`p-2 rounded-lg border-2 text-xs font-medium transition-all ${
                          reservationForm.reservation_type === type.value
                            ? 'border-[#C2410C] bg-orange-50'
                            : 'border-stone-200 hover:border-stone-300'
                        }`}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Input
                      type="date"
                      value={reservationForm.date}
                      onChange={(e) => setReservationForm({ ...reservationForm, date: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tijd</Label>
                    <Input
                      type="time"
                      value={reservationForm.time}
                      onChange={(e) => setReservationForm({ ...reservationForm, time: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Naam gast</Label>
                    <Input
                      value={reservationForm.guest_name}
                      onChange={(e) => setReservationForm({ ...reservationForm, guest_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Aantal personen</Label>
                    <Select value={reservationForm.guest_count} onValueChange={(value) => setReservationForm({ ...reservationForm, guest_count: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10,12,15,20,25,30].map(n => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Telefoon</Label>
                    <Input
                      value={reservationForm.phone}
                      onChange={(e) => setReservationForm({ ...reservationForm, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={reservationForm.email}
                      onChange={(e) => setReservationForm({ ...reservationForm, email: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Opmerkingen</Label>
                  <Textarea
                    value={reservationForm.notes}
                    onChange={(e) => setReservationForm({ ...reservationForm, notes: e.target.value })}
                    rows={2}
                  />
                </div>
                <Button type="submit" className="w-full btn-primary">Reserveren</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <span className="text-amber-800 font-medium">
            {pendingCount} {pendingCount === 1 ? 'reservering wacht' : 'reserveringen wachten'} op goedkeuring
          </span>
          {!emailConfigured && (
            <span className="text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded">
              E-mail niet geconfigureerd
            </span>
          )}
          <Button 
            variant="outline" 
            size="sm" 
            className="ml-auto text-amber-700 border-amber-300"
            onClick={() => setActiveTab('lijst')}
          >
            Bekijken
          </Button>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="kpi-tile">
          <p className="kpi-label">Totaal Reserveringen</p>
          <p className="kpi-value">{totalReservations}</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Bevestigd</p>
          <p className="kpi-value text-emerald-600">{confirmedReservations}</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">In Afwachting</p>
          <p className="kpi-value text-amber-600">{pendingReservations}</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">Verwachte Gasten</p>
          <p className="kpi-value">{totalExpectedGuests}</p>
        </div>
        <div className="kpi-tile">
          <p className="kpi-label">No-show %</p>
          <p className="kpi-value">{formatPercentage(noShowPercentage)}</p>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="agenda">Agenda</TabsTrigger>
          <TabsTrigger value="lijst">
            Lijst {pendingCount > 0 && <Badge className="ml-2 bg-amber-500">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="bezetting">Bezetting</TabsTrigger>
        </TabsList>

        {/* Agenda Tab */}
        <TabsContent value="agenda">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="icon" onClick={navigatePrev}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={goToToday}>Vandaag</Button>
                  <Button variant="outline" size="icon" onClick={navigateNext}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <h2 className="text-lg font-semibold ml-4">
                    {agendaView === 'day' && format(currentDate, 'EEEE d MMMM yyyy', { locale: nl })}
                    {agendaView === 'week' && `${format(startOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMM', { locale: nl })} - ${format(endOfWeek(currentDate, { weekStartsOn: 1 }), 'd MMM yyyy', { locale: nl })}`}
                    {agendaView === 'month' && format(currentDate, 'MMMM yyyy', { locale: nl })}
                  </h2>
                </div>
                <div className="flex gap-1 bg-stone-100 p-1 rounded-lg">
                  <Button 
                    variant={agendaView === 'day' ? 'default' : 'ghost'} 
                    size="sm"
                    onClick={() => setAgendaView('day')}
                    className={agendaView === 'day' ? 'bg-white shadow-sm' : ''}
                  >
                    Dag
                  </Button>
                  <Button 
                    variant={agendaView === 'week' ? 'default' : 'ghost'} 
                    size="sm"
                    onClick={() => setAgendaView('week')}
                    className={agendaView === 'week' ? 'bg-white shadow-sm' : ''}
                  >
                    Week
                  </Button>
                  <Button 
                    variant={agendaView === 'month' ? 'default' : 'ghost'} 
                    size="sm"
                    onClick={() => setAgendaView('month')}
                    className={agendaView === 'month' ? 'bg-white shadow-sm' : ''}
                  >
                    Maand
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day View */}
              {agendaView === 'day' && (
                <div className="space-y-2">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm text-[#78716C]">{getReservationsForDate(currentDate).length} reserveringen</span>
                    <Button size="sm" variant="outline" onClick={() => openQuickAdd(currentDate)}>
                      <Plus className="w-4 h-4 mr-1" /> Toevoegen
                    </Button>
                  </div>
                  {getReservationsForDate(currentDate).length === 0 ? (
                    <div className="text-center py-12 text-[#78716C]">Geen reserveringen voor deze dag</div>
                  ) : (
                    <div className="space-y-2">
                      {getReservationsForDate(currentDate).map(res => (
                        <div 
                          key={res.id}
                          onClick={() => { setSelectedReservation(res); setDetailDialog(true); }}
                          className="flex items-center gap-4 p-4 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer transition-colors"
                        >
                          <div className="text-lg font-semibold text-[#C2410C] w-16">{res.time}</div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{res.guest_name}</span>
                              <Badge className={getTypeConfig(res.reservation_type).color}>{getTypeConfig(res.reservation_type).label}</Badge>
                              <Badge className={statusConfig[res.status]?.color}>{statusConfig[res.status]?.label}</Badge>
                              {res.source === 'website' && <Badge className="bg-blue-100 text-blue-700"><Globe className="w-3 h-3 mr-1" />Website</Badge>}
                            </div>
                            <div className="text-sm text-[#78716C] mt-1">
                              {res.guest_count} personen {res.phone && `• ${res.phone}`}
                            </div>
                          </div>
                          <Eye className="w-5 h-5 text-[#78716C]" />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Week View */}
              {agendaView === 'week' && (
                <div className="grid grid-cols-7 gap-2">
                  {generateWeekDays().map(day => {
                    const dayReservations = getReservationsForDate(day);
                    const isToday = isSameDay(day, new Date());
                    return (
                      <div 
                        key={day.toISOString()} 
                        className={`min-h-[200px] border rounded-lg overflow-hidden ${isToday ? 'border-[#C2410C] bg-orange-50/30' : 'border-stone-200'}`}
                      >
                        <div className={`p-2 text-center border-b ${isToday ? 'bg-[#C2410C] text-white' : 'bg-stone-50'}`}>
                          <div className="text-xs uppercase">{format(day, 'EEE', { locale: nl })}</div>
                          <div className={`text-lg font-semibold ${isToday ? '' : 'text-[#1C1917]'}`}>{format(day, 'd')}</div>
                        </div>
                        <div className="p-1 space-y-1 max-h-[250px] overflow-y-auto">
                          {dayReservations.slice(0, 5).map(res => (
                            <div 
                              key={res.id}
                              onClick={() => { setSelectedReservation(res); setDetailDialog(true); }}
                              className={`p-1.5 rounded text-xs cursor-pointer hover:opacity-80 ${getTypeConfig(res.reservation_type).color}`}
                            >
                              <div className="font-medium truncate">{res.time} {res.guest_name}</div>
                              <div className="text-[10px] opacity-75">{res.guest_count}p</div>
                            </div>
                          ))}
                          {dayReservations.length > 5 && (
                            <div className="text-xs text-center text-[#78716C]">+{dayReservations.length - 5} meer</div>
                          )}
                          {dayReservations.length === 0 && (
                            <button 
                              onClick={() => openQuickAdd(day)}
                              className="w-full p-2 text-xs text-[#78716C] hover:text-[#C2410C] hover:bg-stone-100 rounded"
                            >
                              + Toevoegen
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Month View */}
              {agendaView === 'month' && (
                <div>
                  <div className="grid grid-cols-7 gap-1 mb-1">
                    {['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'].map(d => (
                      <div key={d} className="text-center text-xs font-medium text-[#78716C] py-2">{d}</div>
                    ))}
                  </div>
                  <div className="grid grid-cols-7 gap-1">
                    {generateMonthDays().map(day => {
                      const dayReservations = getReservationsForDate(day);
                      const isToday = isSameDay(day, new Date());
                      const isCurrentMonth = isSameMonth(day, currentDate);
                      return (
                        <div 
                          key={day.toISOString()}
                          onClick={() => { setCurrentDate(day); setAgendaView('day'); }}
                          className={`min-h-[80px] p-1 border rounded cursor-pointer transition-colors ${
                            isToday ? 'border-[#C2410C] bg-orange-50' : 
                            isCurrentMonth ? 'border-stone-200 hover:bg-stone-50' : 'border-stone-100 bg-stone-50/50'
                          }`}
                        >
                          <div className={`text-sm font-medium mb-1 ${isCurrentMonth ? 'text-[#1C1917]' : 'text-[#A8A29E]'}`}>
                            {format(day, 'd')}
                          </div>
                          {dayReservations.length > 0 && (
                            <div className="space-y-0.5">
                              {dayReservations.slice(0, 2).map(res => (
                                <div key={res.id} className={`text-[10px] px-1 py-0.5 rounded truncate ${getTypeConfig(res.reservation_type).color}`}>
                                  {res.time} {res.guest_name}
                                </div>
                              ))}
                              {dayReservations.length > 2 && (
                                <div className="text-[10px] text-[#78716C]">+{dayReservations.length - 2}</div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Type Legend */}
          <div className="flex flex-wrap gap-2 mt-4">
            {reservationTypes.map(type => (
              <Badge key={type.value} className={type.color}>{type.label}</Badge>
            ))}
          </div>
        </TabsContent>

        {/* List Tab */}
        <TabsContent value="lijst">
          <Card>
            <CardContent className="pt-6">
              {/* Search Section */}
              <div className="mb-6 space-y-4" data-testid="reservation-search-section">
                {/* Main search bar */}
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#78716C]" />
                    <Input
                      placeholder="Zoek op naam, telefoon of reserveringsnummer..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-11"
                      data-testid="search-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <div className="w-40">
                      <Input
                        type="date"
                        value={searchDate}
                        onChange={(e) => setSearchDate(e.target.value)}
                        className="h-11"
                        data-testid="search-date"
                      />
                    </div>
                    <Select value={searchGuestCount} onValueChange={setSearchGuestCount}>
                      <SelectTrigger className="w-36 h-11" data-testid="search-guest-count">
                        <Users className="w-4 h-4 mr-2 text-[#78716C]" />
                        <SelectValue placeholder="Personen" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Alle</SelectItem>
                        {[1,2,3,4,5,6,7,8,9,10,12,15,20,25,30].map(n => (
                          <SelectItem key={n} value={String(n)}>{n} {n === 1 ? 'persoon' : 'personen'}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {hasActiveFilters && (
                      <Button 
                        variant="outline" 
                        onClick={clearSearch}
                        className="h-11"
                        data-testid="clear-search-btn"
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Wissen
                      </Button>
                    )}
                  </div>
                </div>
                
                {/* Search results info */}
                {hasActiveFilters && (
                  <div className="flex items-center gap-2 text-sm text-[#78716C] bg-stone-50 px-3 py-2 rounded-lg">
                    <Filter className="w-4 h-4" />
                    <span>
                      {getFilteredReservations().length} {getFilteredReservations().length === 1 ? 'reservering' : 'reserveringen'} gevonden
                      {searchQuery && <span> voor "{searchQuery}"</span>}
                      {searchDate && <span> op {formatDate(searchDate)}</span>}
                      {searchGuestCount && searchGuestCount !== 'all' && <span> met {searchGuestCount} personen</span>}
                    </span>
                  </div>
                )}
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Datum & Tijd</TableHead>
                    <TableHead>Gast</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Personen</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Bron</TableHead>
                    <TableHead className="text-right">Acties</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getFilteredReservations().length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-[#78716C]">
                        {hasActiveFilters 
                          ? 'Geen reserveringen gevonden voor deze zoekopdracht'
                          : 'Geen reserveringen'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    getFilteredReservations().map(res => (
                    <TableRow key={res.id} className={res.status === 'in_afwachting' ? 'bg-amber-50' : ''} data-testid={`reservation-row-${res.id}`}>
                      <TableCell>
                        <div className="font-medium">{formatDate(res.date)}</div>
                        <div className="text-sm text-[#78716C]">{res.time}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{res.guest_name}</div>
                        {res.phone && <div className="text-sm text-[#78716C]">{res.phone}</div>}
                      </TableCell>
                      <TableCell>
                        <Badge className={getTypeConfig(res.reservation_type).color}>
                          {getTypeConfig(res.reservation_type).label}
                        </Badge>
                      </TableCell>
                      <TableCell>{res.guest_count}</TableCell>
                      <TableCell>
                        <Badge className={statusConfig[res.status]?.color}>
                          {statusConfig[res.status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {res.source === 'website' ? (
                          <Badge className="bg-blue-100 text-blue-700"><Globe className="w-3 h-3 mr-1" />Website</Badge>
                        ) : (
                          <span className="text-sm text-[#78716C]">Intern</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {res.status === 'in_afwachting' && (
                            <>
                              <Button size="icon" variant="ghost" className="text-emerald-600 hover:bg-emerald-50" onClick={() => handleUpdateStatus(res.id, 'bevestigd')}>
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="text-red-600 hover:bg-red-50" onClick={() => handleUpdateStatus(res.id, 'geannuleerd')}>
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          <Button size="icon" variant="ghost" onClick={() => { setSelectedReservation(res); setDetailDialog(true); }}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Occupancy Tab */}
        <TabsContent value="bezetting">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Bezettingsgraad</CardTitle>
                <Dialog open={occupancyDialog} onOpenChange={setOccupancyDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Plus className="w-4 h-4 mr-1" /> Invoeren
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Bezettingsgraad invoeren</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddOccupancy} className="space-y-4 pt-4">
                      <div className="space-y-2">
                        <Label>Datum</Label>
                        <Input
                          type="date"
                          value={occupancyForm.date}
                          onChange={(e) => setOccupancyForm({ ...occupancyForm, date: e.target.value })}
                          required
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Werkelijke gasten</Label>
                          <Input
                            type="number"
                            value={occupancyForm.actual_guests}
                            onChange={(e) => setOccupancyForm({ ...occupancyForm, actual_guests: e.target.value })}
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Capaciteit</Label>
                          <Input
                            type="number"
                            value={occupancyForm.capacity}
                            onChange={(e) => setOccupancyForm({ ...occupancyForm, capacity: e.target.value })}
                            required
                          />
                        </div>
                      </div>
                      <Button type="submit" className="w-full btn-primary">Opslaan</Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                {occupancy.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={occupancy.sort((a, b) => new Date(a.date) - new Date(b.date)).slice(-14).map(o => ({
                      date: format(new Date(o.date), 'dd/MM', { locale: nl }),
                      bezetting: o.capacity > 0 ? Math.round((o.actual_guests / o.capacity) * 100) : 0
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis domain={[0, 100]} />
                      <Tooltip formatter={(value) => [`${value}%`, 'Bezetting']} />
                      <Bar dataKey="bezetting" fill="#C2410C" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center py-12 text-[#78716C]">Nog geen bezettingsdata</div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recente bezetting</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {occupancy.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 7).map(o => {
                    const percentage = o.capacity > 0 ? (o.actual_guests / o.capacity) * 100 : 0;
                    return (
                      <div key={o.id} className="flex items-center gap-4">
                        <div className="w-20 text-sm font-medium">{formatDate(o.date)}</div>
                        <div className="flex-1">
                          <Progress value={percentage} className="h-2" />
                        </div>
                        <div className="w-24 text-right">
                          <span className="text-sm font-medium">{o.actual_guests}/{o.capacity}</span>
                          <span className="text-xs text-[#78716C] ml-1">({Math.round(percentage)}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Reservation Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reservering Details</DialogTitle>
          </DialogHeader>
          {selectedReservation && (
            <div className="space-y-4 pt-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#C2410C] to-[#D97706] flex items-center justify-center text-white font-bold text-lg">
                  {selectedReservation.guest_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 className="text-lg font-bold">{selectedReservation.guest_name}</h3>
                  <div className="flex items-center gap-2">
                    <Badge className={getTypeConfig(selectedReservation.reservation_type).color}>
                      {getTypeConfig(selectedReservation.reservation_type).label}
                    </Badge>
                    <Badge className={statusConfig[selectedReservation.status]?.color}>
                      {statusConfig[selectedReservation.status]?.label}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="bg-stone-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-3">
                  <CalendarIcon className="w-4 h-4 text-[#C2410C]" />
                  <span className="text-sm">{formatDate(selectedReservation.date)} om {selectedReservation.time}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Users className="w-4 h-4 text-[#C2410C]" />
                  <span className="text-sm">{selectedReservation.guest_count} personen</span>
                </div>
                {selectedReservation.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-4 h-4 text-[#C2410C]" />
                    <span className="text-sm">{selectedReservation.phone}</span>
                  </div>
                )}
                {selectedReservation.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-4 h-4 text-[#C2410C]" />
                    <span className="text-sm">{selectedReservation.email}</span>
                  </div>
                )}
                {selectedReservation.source === 'website' && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-4 h-4 text-blue-600" />
                    <span className="text-sm text-blue-600">Via publieke website</span>
                  </div>
                )}
              </div>

              {selectedReservation.notes && (
                <div className="space-y-1">
                  <Label className="text-xs text-[#78716C]">Opmerkingen</Label>
                  <p className="text-sm p-3 bg-stone-50 rounded-lg">{selectedReservation.notes}</p>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2 border-t">
                {selectedReservation.status === 'in_afwachting' && (
                  <>
                    <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => handleUpdateStatus(selectedReservation.id, 'bevestigd')}>
                      <Check className="w-4 h-4 mr-1" /> Goedkeuren
                    </Button>
                    <Button variant="outline" className="flex-1 text-red-600" onClick={() => handleUpdateStatus(selectedReservation.id, 'geannuleerd')}>
                      <X className="w-4 h-4 mr-1" /> Afwijzen
                    </Button>
                  </>
                )}
                {selectedReservation.status === 'bevestigd' && (
                  <>
                    <Button variant="outline" className="text-orange-600" onClick={() => handleUpdateStatus(selectedReservation.id, 'no-show')}>
                      No-show
                    </Button>
                    <Button variant="outline" className="text-blue-600" onClick={() => handleUpdateStatus(selectedReservation.id, 'voltooid')}>
                      Voltooid
                    </Button>
                  </>
                )}
                <Button variant="outline" className="text-red-600" onClick={() => handleDeleteReservation(selectedReservation.id)}>
                  <Trash2 className="w-4 h-4 mr-1" /> Verwijderen
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ReserveringenBezettingPage;
